import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import About from "../components/About";
import Skills from "../components/Skills";
import Projects from "../components/Projects";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import FloatingHireMeButton from "../components/FloatingHireMeButton";
import { portfolioAPI } from "../services/api";

const fallbackData = {
  hero: {
    badge: "🎮 Game Developer",
    title: "Hi, I’m Abdul Haseeb",
    subtitle: "I’m a passionate Game Developer specialized in Unity & C#, creating 2D/3D worlds, smooth UI animations with DOTween, and optimizing performance for an immersive gaming experience 🚀.",
    resume_url: "/AbdulResume.pdf",
    profile_image: "Profile.jpeg"
  },
  about: {
    heading: "About Me",
    description: "Hi, I’m Abdul Haseeb, a Game Developer and Full-Stack Engineer. I specialize in creating immersive 2D & 3D games using Unity and DOTween, while also building scalable web & mobile apps with modern stacks. My expertise lies in combining performance optimization, clean coding to deliver engaging digital experiences 🚀.",
    about_image: "/about.png"
  },
  highlights: [
    { icon_name: "FaUnity", text: "Unity (2D & 3D Game Dev)" },
    { icon_name: "MdAnimation", text: "DOTween (Animations & UI)" },
    { icon_name: "FaGithub", text: "GitHub (Version Control)" },
    { icon_name: "GiLightningFrequency", text: "Optimization (Mobile/PC)" },
    { icon_name: "SiFirebase", text: "Firebase (Cloud & Auth)" },
    { icon_name: "FaCode", text: "General Coding & Algorithms" }
  ],
  skills: [
    { id: 1, name: "Unity Engine", desc: "2D & 3D Game Development", level: 90, icon_name: "FaUnity", category: "Game Dev" },
    { id: 2, name: "C# / .NET", desc: "Core programming for gameplay & systems", level: 85, icon_name: "SiDotnet", category: "Programming" },
    { id: 3, name: "DOTween", desc: "Smooth animations & UI transitions", level: 80, icon_name: "MdAnimation", category: "Tools" },
    { id: 4, name: "Git/GitHub", desc: "Version control & collaboration", level: 75, icon_name: "FaGithub", category: "Tools" },
    { id: 5, name: "Optimization", desc: "Performance tuning for mobile & PC", level: 70, icon_name: "GiLightningFrequency", category: "Programming" },
    { id: 6, name: "Firebase", desc: "Auth & cloud-based services for games", level: 65, icon_name: "SiFirebase", category: "Cloud" },
    { id: 7, name: "General Coding", desc: "Problem solving & algorithms", level: 78, icon_name: "FaCode", category: "Programming" }
  ],
  projects: [
    { id: 1, title: "Private Company Website", desc: "An advanced portfolio with AI-powered features, dark/light mode, and interactive animations.", video_url: "https://www.youtube.com/embed/dQw4w9WgXcQ", demo_url: "https://www.chromapigmentanddyestuff.com/", code_url: "https://github.com/your-repo", tags: "PYTHON,HTML,CSS" },
    { id: 2, title: "E-Commerce Platform", desc: "Full-stack MERN e-commerce app with payments, cart system, and admin dashboard.", video_url: "https://www.youtube.com/embed/ysz5S6PUM-U", demo_url: "#", code_url: "#", tags: "MongoDB,Express,React,Node.js" },
    { id: 3, title: "Real-Time Chat App", desc: "Socket.io based chat app with groups, emojis, and notifications.", video_url: "https://www.youtube.com/embed/ScMzIvxBSi4", demo_url: "#", code_url: "#", tags: "Node.js,Socket.io,MongoDB" },
    { id: 4, title: "Flutter Mobile App", desc: "Cross-platform Flutter app with Firebase authentication and push notifications.", video_url: "https://www.youtube.com/embed/aqz-KE-bpKQ", demo_url: "#", code_url: "#", tags: "Flutter,Firebase" }
  ],
  contact: {
    email: "abdulhaseeb0847179@gmail.com",
    phone: "+92 307 4058316",
    location: "Lahore, Pakistan",
    facebook: "https://www.facebook.com/share/1BfvWMhmdy/",
    whatsapp: "https://wa.me/923074058316",
    github: "https://share.google/z4idK2IpJcDlV6hmP",
    linkedin: "https://www.linkedin.com/in/abdul-haseeb-533302214?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
  }
};

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await portfolioAPI.getPortfolioData();
        setPortfolioData(data);
      } catch (err) {
        console.warn("Failed to fetch from API, falling back to static local data:", err);
        setPortfolioData(fallbackData);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center">
        {/* Loading Spinner */}
        <div className="relative w-16 h-16">
          <div className="w-16 h-16 rounded-full border-4 border-indigo-200 dark:border-indigo-900 border-t-indigo-600 animate-spin" />
          <div className="absolute inset-0 w-16 h-16 rounded-full border border-purple-500 blur-sm opacity-50 animate-pulse" />
        </div>
        <p className="mt-4 font-[Poppins] text-indigo-600 dark:text-indigo-400 font-medium tracking-wide animate-pulse">
          Loading Portfolio...
        </p>
      </div>
    );
  }

  // Safeguards for loaded state
  const { hero, about, highlights, skills, projects, contact } = portfolioData || fallbackData;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-500">
      <Navbar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      {activeSection === "home" && (
        <Hero 
          setActiveSection={setActiveSection} 
          heroData={hero}
        />
      )}
      {activeSection === "about" && (
        <About 
          aboutData={about} 
          highlightsData={highlights}
        />
      )}
      {activeSection === "skills" && (
        <Skills 
          skillsData={skills}
        />
      )}
      {activeSection === "projects" && (
        <Projects 
          projectsData={projects}
        />
      )}
      {activeSection === "contact" && (
        <Contact 
          contactData={contact}
        />
      )}

      <Footer 
        setActiveSection={setActiveSection} 
        contactData={contact}
      />

      <FloatingHireMeButton
        setActiveSection={setActiveSection}
        setIsMenuOpen={setIsMenuOpen}
      />
    </div>
  );
}
